/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @Description
 * @Version
 * @Author  Xu Zepeng
 * @date 2023/5/23 11:49
 */

interface SocketOptions {
  resetTime: number;
  timeout: number;
}

interface PrintMessage {
  apiName?: string;
  resultAck?: {
    errorCode?: number;
    info?: string;
    callback?: (msg: PrintMessage) => void;
  };
  [key: string]: any;
}

interface PromisePoolItem {
  timestamp: number;
  content: any;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeoutCallback: ReturnType<typeof setTimeout>;
}

export default class Socket {
  // Whether processing commit job default state, default processed
  public isProcessingCommitJob: boolean = false;
  // Current websocket url
  public url: string;
  // Reconnect time config, timeout config
  public options: SocketOptions = { resetTime: 3000, timeout: 10000 };
  // Whether auto close
  public customClose: boolean = false;
  // promise storage pool
  private promisePool: { [key: string]: PromisePoolItem } = {};
  // websocket instance
  private _websocket: WebSocket | undefined;
  // Add print listener callback storage
  public printListeners: Set<(msg: PrintMessage) => void> = new Set();
  // Save callback function reference
  private openChangeCallback: ((isOpen: boolean) => void) | null = null;

  constructor(options: Partial<SocketOptions> = {}) {
    this.url = 'ws://127.0.0.1:37989';
    this.options = { ...this.options, ...options };
  }

  // Determine if return data is json
  private isJSON(str: any): any | boolean {
    if (typeof str === 'string') {
      try {
        return JSON.parse(str);
      } catch {
        return false;
      }
    } else {
      return false;
    }
  }

  // Close callback processing
  private closeCallback(): void {
    if (!this.customClose) {
      this.isProcessingCommitJob = false; // Reset commit status
      this.close(false);
      this._websocket = undefined;
      this.printListeners.clear(); // Clear all callbacks
      const timer = setTimeout(async () => {
        try {
          await this.open(this.openChangeCallback || undefined, undefined);
          clearTimeout(timer);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Reconnect failed:', error);
          if (this.openChangeCallback) {
            this.openChangeCallback(false);
          }
        }
      }, this.options.resetTime);
      this.customClose = false;
    }
  }

  /**
   * Open WebSocket connection and return a Promise resolving to WebSocket instance.
   *
   * @param {function} openChange - Callback called when WebSocket connection opens.
   * @param {function} onMessageCallback - Callback called when message is received.
   * @return {Promise} A Promise resolving to WebSocket instance.
   */
  public open(openChange?: (isOpen: boolean) => void, onMessageCallback?: (msg: any) => void): Promise<{ e: Event, ws: Socket }> {
    if (openChange) {
      this.openChangeCallback = openChange;
    }
    return new Promise((resolve) => {
      if (typeof this._websocket === 'undefined') {
        this._websocket = new WebSocket(this.url);
        this._websocket.onopen = (e: Event) => {
          // eslint-disable-next-line no-console
          console.log(openChange, 'open---openChange');
          if (openChange) {
            openChange(true);
          }
          resolve({ e, ws: this });
        };
        this._websocket.onerror = () => {
          if (openChange) {
            openChange(false);
          }
          this.isProcessingCommitJob = false; // Reset status on exception
          this.closeCallback();
        };
        this._websocket.onclose = () => {
          if (openChange) {
            openChange(false);
          }
          this.printListeners.clear();
          this.closeCallback();
        };
        this._websocket.onmessage = (e: MessageEvent) => {
          const msg = this.isJSON(e.data) ? JSON.parse(e.data as string) : e.data;
          // New message classification router
          this.messageRouter(msg, onMessageCallback);
        };
      }
    });
  }

  /**
   * Message router core method
   * Function: Classify messages into three types based on characteristics
   * 1. API response message (match apiName field)
   * 2. Device active report event (match resultAck.callback structure)
   * 3. Unified callback processing (catch-all for all messages)
   */
  private messageRouter(msg: any, onMessageCallback?: (msg: any) => void): void {

    // Whitelist mechanism for active report messages
    const isAutoReport = msg.apiName === 'commitJob';

    if (msg.apiName && msg.apiName !== 'getPrinterHighLevelInfo' && msg.apiName !== 'printStatus' && !isAutoReport) { // Handle normal API response
      this.handleApiResponse(msg);
    } else if (isAutoReport) { // Handle device active report
      this.handleEventPush(msg);
    }
    if (msg.apiName === 'getPrinterHighLevelInfo' || msg.apiName === 'printStatus') { // Corrected logic here
      if (onMessageCallback) {
        onMessageCallback(msg); // Unified callback to handle device status report
      }
    }
  }

  // API response processor
  private handleApiResponse(msg: any): void {
    const req = this.promisePool[msg.apiName];
    if (!req) return;

    // Handle commitJob special logic
    if (msg.apiName === 'commitJob') {
      if (msg.resultAck?.info === 'commitJob ok!') {
        req.resolve(msg);
        this.cleanupRequest(msg.apiName, req);
        this.isProcessingCommitJob = true;
      }
    } else {
      if (msg.resultAck?.errorCode !== 0) {
        this.isProcessingCommitJob = false;
      }
      req.resolve(msg);
      this.cleanupRequest(msg.apiName, req);
    }
  }

  // Event push processor
  private handleEventPush(msg: any): void {
    // Handle commitJob special logic
    this.printListeners.forEach(listener => listener(msg));
    // Error status handling
    if (msg.resultAck?.errorCode !== 0) {
      this.isProcessingCommitJob = false;
    }
  }

  // New request cleanup method
  private cleanupRequest(apiName: string, req: PromisePoolItem): void {
    if (req) {
      clearTimeout(req.timeoutCallback);
    }
    delete this.promisePool[apiName];
  }

  // Close websocket connection, closing=true means manual close
  public close(closing: boolean = true): void {
    this.customClose = closing;
    if (this._websocket && this._websocket.readyState === WebSocket.OPEN) {
      this.clearAllListeners();
      if (this.openChangeCallback) {
        this.openChangeCallback(false);
      }
      this._websocket.close();
    }
  }
  // content contains all data sent to ws
  public send(content: any, timeout: number | null = null): Promise<any> {
    // Record timestamp of current request for timeout check
    const timestamp = new Date().getTime();
    // If no message returned from ws after this.options.timeout seconds
    const timeoutCallback = setTimeout(
      () => {
        // After initiating commitJob, restore processing status to unprocessed to handle exception messages returned immediately after non-commit data
        if (content.apiName === 'commitJob') {
          this.isProcessingCommitJob = true;
        }
        const req = this.promisePool[content.apiName];
        if (req && req.timestamp === timestamp) {
          req.resolve({
            apiName: content.apiName,
            resultAck: { errorCode: 22 },
            Error: 'Print timeout',
          });
          clearTimeout(req.timeoutCallback);
          delete this.promisePool[content.apiName];
        }
      },
      timeout !== null ? timeout : this.options.timeout
    );
    return new Promise((resolve, reject) => {
      this.promisePool[content.apiName] = {
        timestamp,
        content,
        resolve,
        reject,
        timeoutCallback,
      };

      if (this._websocket && this._websocket.readyState === WebSocket.OPEN) {
        this._websocket.send(JSON.stringify({ ...content }));
      } else {
        this.promisePool[content.apiName].resolve({
          apiName: content.apiName,
          resultAck: { errorCode: 22 },
          Error: 'Print timeout',
        });
      }
    });
  }
  // Custom message send
  public customSend(content: any): void {
    if (this._websocket && this._websocket.readyState === WebSocket.OPEN) {
      this._websocket.send(JSON.stringify({ ...content }));
    }
  }
  // Add print listener method
  public addPrintListener(callback: (msg: PrintMessage) => void): (msg: PrintMessage) => void {
    if (typeof callback !== 'function') {
      // eslint-disable-next-line no-console
      console.error('addPrintListener: callback must be a function');
      return callback; // Or throw error
    }

    // Remove existing same callback first
    this.printListeners.delete(callback);
    // Add new callback
    this.printListeners.add(callback);

    // Return function to remove listener
    return callback;
  }
  // Remove print listener method
  /**
   * Remove specified print listener callback
   * @param {Function} callback - Callback function to remove
   * @returns {void}
   */
  public removePrintListener(callback: (msg: PrintMessage) => void): void {
    // Parameter check: callback must exist
    if (!callback) {
      // eslint-disable-next-line no-console
      console.error('removePrintListener: callback is required');
      return;
    }

    // Try to remove callback from listener set
    const removed = this.printListeners.delete(callback);

    // Output warning if callback not found
    if (!removed) {
      // eslint-disable-next-line no-console
      console.warn('removePrintListener: callback not found in listeners');
    }
  }
  // Add method for debugging
  public getListenersCount(): number {
    return this.printListeners.size;
  }
  // Modify method to clear all listeners
  public clearAllListeners(): void {
    const count = this.printListeners.size;
    this.printListeners.clear();
    // eslint-disable-next-line no-console
    console.log(`Cleared ${count} listeners`);
  }
}
